import React, { FC, useCallback, useContext, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import { LicenseContext } from '../../contexts/LicenseContext';
import { useApiErrorHandler } from '../../hooks/useApiErrorHandler';
import { FileInput } from '../FileInput/FileInput';
import { useTranslation } from 'react-i18next';
import { DangerousButton } from '../CustomButtons/DangerousButton';
import { Link } from 'react-router-dom';
import { ConfirmationDialog } from '../ConfirmationDialog/ConfirmationDialog';
import { LicensesIncludedInOffline } from './LicensesIncludedInOffline';
import { apiLicense } from '../../resources/api/apiLicense';
import { useDialog } from '../../hooks/useDialog';
import { OrganizationContext } from '../../contexts/OrganizationContext';
import { usePackagesInSameLicenseFile } from '../../hooks/usePackagesInSameLicenseFile';
import { legacyReleaseFileExtension } from '../../const/airgap';
import { LicensesContext } from '../../contexts/LicensesContext';

interface IProps {
  onClose: () => void;
}

const { useReleaseAirgap, useReleaseAirgapLegacy, useForceRelease } = apiLicense;

export const ReleaseOfflineLicense: FC<IProps> = ({ onClose }) => {
  const [file, setFile] = useState<File | null>(null);
  const apiErrorHandler = useApiErrorHandler();
  const { t } = useTranslation();
  const { onRefresh } = useContext(LicensesContext);

  const { organization } = useContext(OrganizationContext);
  const license = useContext(LicenseContext);
  const { isOpen: isForce, openUrl: forceUrl, close: abortForce } = useDialog('force');

  const { execute: forceRelease, Guard: GuardForce } = useForceRelease(
    organization ?? void 0,
    license?.userUuid,
    license?.id
  );
  const { execute: release, Guard: GuardRelease } = useReleaseAirgap(organization ?? void 0, license?.userUuid);
  const { execute: releaseLegacy } = useReleaseAirgapLegacy(organization ?? void 0, license?.userUuid);

  const toBeReleased = usePackagesInSameLicenseFile();

  const onUpload = useCallback(async () => {
    if (file && license?.id) {
      try {
        if (file.name.endsWith(legacyReleaseFileExtension)) {
          await releaseLegacy(file, license.id);
        } else {
          await release(file, toBeReleased.groupedIds.basePackages, toBeReleased.groupedIds.addonPackages);
        }
        await onRefresh();
      } catch (e) {
        apiErrorHandler(e, t('licenses.releaseDialog.failed'));
      }
    }
  }, [file, release, releaseLegacy, toBeReleased]);

  const onOfflineForceRelease = useCallback(async () => {
    try {
      await forceRelease();
      await onRefresh();
      onClose();
    } catch (e) {
      apiErrorHandler(e);
    }
  }, [forceRelease]);

  return (
    <>
      <DialogTitle>{t('licenses.releaseDialog.title')}</DialogTitle>
      <DialogContent dividers>
        <Typography paragraph>{t('licenses.releaseDialog.description')}</Typography>
        <Box p={1} textAlign="center">
          <img src="/release-step2.svg" />
        </Box>
        <Typography paragraph>{t('licenses.releaseDialog.content.upload')}</Typography>

        <LicensesIncludedInOffline />

        <FileInput
          value={file}
          onChange={setFile}
          label={t('licenses.releaseDialog.fileInputLabel')}
          id="license-release-file"
          changeLabel={t('licenses.releaseDialog.fileInputChangeLabel')}
          accept={'.release,.pxrelease'}
        />
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose}>
          {t('common.dialog.close')}
        </Button>
        <GuardForce>
          <DangerousButton component={Link} to={forceUrl} replace={true}>
            {t('licenses.releaseDialog.force.button')}
          </DangerousButton>

          <ConfirmationDialog
            open={isForce}
            title={t('licenses.releaseDialog.force.title')}
            message={t('licenses.releaseDialog.force.message')}
            confirm={onOfflineForceRelease}
            abort={abortForce}
            dangerous
          />
        </GuardForce>
        <GuardRelease>
          <Button variant="contained" color="primary" disabled={!file} onClick={onUpload}>
            {t('licenses.releaseDialog.confirmRelease')}
          </Button>
        </GuardRelease>
      </DialogActions>
    </>
  );
};
